import { validateId } from '~/repositories/utils';
import { BaseRestRepository } from './baseRestRepository';
import { uploadSessionRepository } from './uploadSessionRepository';
import type { EntityType as FileEntityType, FileAttachmentDto, MagicUserType } from '~/swagger/portal/Api';

const ATTACHMENTS_URL = '/api/file-attachments';

type EntityType = `${FileEntityType}`;

type AttachmentUploadItem = {
  file: File;
  description?: string;
  shareTypes?: `${MagicUserType}`[];
};

export class AttachmentsRepository extends BaseRestRepository {
  public async getAttachments(entityType: EntityType, entityId: string): Promise<FileAttachmentDto[]> {
    validateId(entityId, 'Attachment entity');
    const url = `${ATTACHMENTS_URL}/${entityType}/${entityId}`;
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async addAttachments(
    entityType: EntityType,
    entityId: string,
    items: AttachmentUploadItem[],
    signal?: AbortSignal
  ): Promise<FileAttachmentDto[]> {
    validateId(entityId, 'Attachment entity');
    if (items.length === 0) return [];

    const sessionUrl = `${ATTACHMENTS_URL}/${entityType}/${entityId}/upload-sessions`;
    const sessionIds = await uploadSessionRepository.uploadFiles(
      sessionUrl,
      items.map((item) => item.file),
      { abortSignal: signal }
    );

    const url = `${ATTACHMENTS_URL}/${entityType}/${entityId}`;
    return Promise.all(
      sessionIds.map(async (uploadSessionId, index) => {
        const response = await this.fetchWithAuth(url, {
          method: 'POST',
          body: JSON.stringify({
            uploadSessionId,
            description: items[index]?.description,
            shareTypes: items[index]?.shareTypes,
          }),
        });
        return this.getJsonResponse(response);
      })
    );
  }

  public async createAttachmentFromTemplate(
    entityType: EntityType,
    entityId: string,
    payload: { letterDocumentTemplateId: string; fileName?: string }
  ): Promise<FileAttachmentDto> {
    validateId(entityId, 'Attachment entity');
    validateId(payload.letterDocumentTemplateId, 'Document template');

    const url = `${ATTACHMENTS_URL}/${entityType}/${entityId}/from-template`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify({
        letterDocumentTemplateId: payload.letterDocumentTemplateId,
        fileName: payload.fileName || undefined,
      }),
    });
    return this.getJsonResponse(response);
  }

  public async updateAttachment(
    entityType: EntityType,
    entityId: string,
    fileAttachmentId: string,
    updateData: { fileName: string; description?: string }
  ): Promise<FileAttachmentDto> {
    validateId(entityId, 'Attachment entity');
    validateId(fileAttachmentId, 'FileAttachment');

    const url = `${ATTACHMENTS_URL}/${entityType}/${entityId}/${fileAttachmentId}`;
    const response = await this.fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify({
        fileName: updateData.fileName,
        description: updateData.description || undefined,
      }),
    });
    return this.getJsonResponse(response);
  }

  public async deleteAttachment(entityType: EntityType, entityId: string, fileAttachmentId: string): Promise<void> {
    validateId(entityId, 'Attachment entity');
    validateId(fileAttachmentId, 'FileAttachment');

    const url = `${ATTACHMENTS_URL}/${entityType}/${entityId}/${fileAttachmentId}`;
    await this.fetchWithAuth(url, { method: 'DELETE' });
  }

  public async setFileShareTypes(
    entityType: EntityType,
    entityId: string,
    fileAttachmentId: string,
    payload: { shareTypes: `${MagicUserType}`[] }
  ): Promise<FileAttachmentDto> {
    validateId(entityId, 'Attachment entity');
    validateId(fileAttachmentId, 'FileAttachment');

    const url = `${ATTACHMENTS_URL}/${entityType}/${entityId}/${fileAttachmentId}/share-types`;
    const response = await this.fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }
}

export const attachmentsRepository = new AttachmentsRepository();
